import { ApiError, AppError, Language, UUID } from '@/types/types';
import { Locale, formatDistance } from 'date-fns';
import { omit } from 'lodash';
import { isAxiosError } from 'axios';
import { DateValue } from '@mantine/dates';
import {
	AvailableAttributeValues,
	CreatorParams,
	CreatorState,
} from '@/types/creator';
import { ErrorCode } from '@/configs/errorCode';
import { ProductType } from '@/api/ProductTypeApi';
import { SerializedRenderer } from '@/renderer/types';
import { Product, ProductyAccessory } from '@/api/ProductApi';
import { CartState } from '@/store/slices/cartSlice';
import { Accessory } from '@/api/AccessoryApi';
import { ProductTypeVariation } from '@/api/ProductTypeVariationApi';
import APP_CONFIG from '@/configs/appConfig';
import moment from 'moment';

export function formatDateForPresentation(date: moment.MomentInput) {
	if (!date) return '-';
	return moment(date).format('DD.MM.YYYY');
}

export function formatTimeForPresentation(
	date: moment.MomentInput | string = new Date()
) {
	return moment(date).format('HH:mm');
}

export function formatExactDate(date: moment.MomentInput = new Date()) {
	return `${formatDateForPresentation(date)}, ${formatTimeForPresentation(
		date
	)}`;
}

const PHONE_REGEX =
	/^\s*(?:\+?(\d{1,3}))?([-. (]*(\d{3})[-. )]*)?((\d{3})[-. ]*(\d{2,4})(?:[-.x ]*(\d+))?)\s*$/;

/**
 *
 * @param phoneNumber validate international
 * @returns
 */
export function validatePhoneNumber(phoneNumber: string) {
	return PHONE_REGEX.test(phoneNumber) && phoneNumber.length >= 9;
}

export function sortByDate(
	dateA: string | Date,
	dateB: string | Date,
	order: 'asc' | 'desc' = 'asc'
) {
	const first = moment(dateA).valueOf(),
		second = moment(dateB).valueOf();

	return order === 'asc' ? first - second : second - first;
}

export function stripHTML(string: string) {
	return string.replace(/(<([^>]+)>)/gi, '');
}

export function fileTypeToHeader(fileType: 'csv' | 'xlsx' | 'pdf') {
	switch (fileType) {
		case 'xlsx':
			return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
		case 'csv':
			return 'text/csv';
		case 'pdf':
			return 'application/pdf';
		default:
			return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
	}
}

export function getContent(conent: Record<string, string>, slug: string) {
	return conent[slug];
}

export function formatDateFromNow(date: Date, locale: Locale) {
	return formatDistance(date, new Date(), {
		locale,
		includeSeconds: true,
		addSuffix: true,
	});
}

export const formatPrice = (value: number, opts?: Intl.NumberFormatOptions) => {
	const formatter = new Intl.NumberFormat('pl', {
		style: 'currency',
		currency: 'PLN',
		currencyDisplay: 'code',
		maximumFractionDigits: !value ? 0 : undefined,
		...opts,
	});

	return formatter.format(value);
};

export const isApiError = (error: unknown): error is ApiError => {
	return (
		!!(error as AppError).status &&
		!!(error as AppError).stack &&
		!!(error as ApiError).message
	);
};

export const isAppError = (error: unknown): error is AppError => {
	return !!(error as AppError).status && !!(error as AppError).stack;
};

export const getErrorCodes = (error: unknown) => {
	if (isAxiosError<ApiError>(error) && error.response)
		return Array.isArray(error.response.data.message)
			? error.response.data.message
			: [error.response.data.message];

	return [ErrorCode.GENERIC];
};

export const errorCodesToMessage = (errorCodes: ErrorCode[]) =>
	errorCodes.map((code) => `errors.${code}`).join(', ');

export function getFileSrc(path?: string) {
	return `${import.meta.env.VITE_API_URL_UPLOADS}${path}`;
}

export function formatDateForApi(date: DateValue) {
	if (!date) return null;

	return moment(date).format('YYYY-MM-DD');
}

export function isDefaultLanguage(language: Language): language is 'PL' {
	return language === APP_CONFIG.DEFAULT_LANGUAGE;
}

export function natSort(nameA = '', nameB = '', order: 'asc' | 'desc' = 'asc') {
	let a: string | number = Number(nameA),
		b: string | number = Number(nameB);

	if (isNaN(a) || isNaN(b)) {
		return order === 'asc'
			? nameA.localeCompare(nameB)
			: nameB.localeCompare(nameA);
	}

	return order === 'asc' ? a - b : b - a;
}

export function getAvailableAttributeValues(productType: ProductType) {
	const availableAttributeValues: AvailableAttributeValues = {
		attributes: [],
		dimensionAttributes: [],
	};

	for (const attributeValue of productType.attributeValues) {
		const availableAttributes = attributeValue.attribute.isDimension
			? availableAttributeValues.dimensionAttributes
			: availableAttributeValues.attributes;

		const attribute = availableAttributes.find(
			(attr) => attr.id === attributeValue.attribute.id
		);

		if (attribute) {
			attribute.attributeValues.push(attributeValue);
			continue;
		}

		availableAttributes.push({
			...attributeValue.attribute,
			attributeValues: [attributeValue],
		});
	}

	availableAttributeValues.attributes.sort((a, b) => a.listOrder - b.listOrder);
	availableAttributeValues.dimensionAttributes.sort(
		(a, b) => a.listOrder - b.listOrder
	);
	for (const attribute of availableAttributeValues.attributes) {
		attribute.attributeValues.sort((a, b) => a.listOrder - b.listOrder);
	}
	for (const attribute of availableAttributeValues.dimensionAttributes) {
		attribute.attributeValues.sort((a, b) => a.listOrder - b.listOrder);
	}

	return availableAttributeValues;
}

export function getInitialSelectedValues(
	productType: ProductType,
	defaultVariation?: ProductTypeVariation
) {
	const selectedValues: CreatorState['selectedValues'] = {};

	for (const attributeValue of productType.attributeValues) {
		selectedValues[attributeValue.attribute.id] = null;
	}

	if (defaultVariation)
		for (const attributeValue of defaultVariation.attributeValues) {
			selectedValues[attributeValue.attribute.id] = attributeValue;
		}

	return selectedValues;
}

export function generateCratorParams(
	state: CreatorState,
	exportedRenderer: SerializedRenderer
): CreatorParams {
	const selectedValues: CreatorParams['selectedValues'] = {};

	let attributeId: UUID;
	for (attributeId in state.selectedValues) {
		const value = state.selectedValues[attributeId];
		selectedValues[attributeId] = value ? value.id : null;
	}

	return {
		selectedValues,
		options: {},
		exportedRenderer,
		creatorAccessories: state.creatorAccessories
			.filter((creatorAccessory) => !creatorAccessory.isTemporary)
			.map((creatorAccessory) => ({
				...omit(creatorAccessory, ['accessory']),
				accessoryId: creatorAccessory.accessory.id,
			})),
	};
}

export function generateProductPayload(
	state: CreatorState,
	exportedRenderer: SerializedRenderer,
	local = false
): Partial<Product> {
	return {
		productTypeId: state.productType!.id,
		productTypeVariationId: state.productTypeVariation!.id,
		productType: { id: state.productType!.id } as ProductType,
		productTypeVariation: {
			id: state.productTypeVariation!.id,
			name: state.productTypeVariation!.name,
		} as ProductTypeVariation,
		productAccessories: state.creatorAccessories.map(
			(creatorAccessory) =>
				({
					refId: creatorAccessory.refId,
					accessory: { id: creatorAccessory.accessory.id } as Accessory,
				}) as ProductyAccessory
		),
		comment: state.comment,
		quantity: state.quantity,
		options: generateCratorParams(state, exportedRenderer),
	};
}

export function saveProjectToLocal(
	state: CreatorState,
	exportedRenderer: SerializedRenderer
) {
	localStorage.setItem(
		APP_CONFIG.AUTO_SAVE.TEMP_PROJECT_KEY,
		JSON.stringify(generateProductPayload(state, exportedRenderer, true))
	);
}

export function loadCartStateFromLocalStorage(): CartState {
	const cartState = localStorage.getItem(APP_CONFIG.CART.KEY);
	if (!cartState) return { items: [] };

	try {
		const payload = JSON.parse(cartState);
		return payload;
	} catch (error) {
		console.error('INVALID_CART_DATA', error);
		return { items: [] };
	}
}

export function getAxes() {
	return ['x', 'y', 'z'];
}
